import React, { Fragment, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { toast } from "../../components/ui-components/Toast";
import AdministrationHeader from "../../components/header-components/AdministrationHeader";
import TableCard from "../../components/ui-components/TableCard";
import EmptyState from "../../components/ui-components/EmptyState";
import Modal from "../../components/ui-components/Modal";
import ConnectionsTable from "./components/ConnectionsTable";
import ConnectionsSummaryStrip from "./components/ConnectionsSummaryStrip";
import AddConnectionWizard from "./components/AddConnectionWizard";
import { getSourceType } from "./sourceTypes";
import { formatRelativeTime } from "../data-imports/utils";
import {
  listSourceConnections,
  deleteSourceConnection,
  validateSourceConnection,
} from "./api";

// Build a lowercase haystack from every column the table renders, so the search box
// matches across all columns (source, name, auth method, status, last sync, import job).
function connectionHaystack(c) {
  const sourceType = getSourceType(c.source_type);
  return [
    sourceType?.name || c.source_type,
    c.connection_name,
    sourceType?.credentialLabel || c.auth_method,
    c.credential_status,
    formatRelativeTime(c.last_sync_at),
    c.import_job_status,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

const SourceConnectionsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showWizard, setShowWizard] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: connections, isLoading, error } = useQuery({
    queryKey: ["source-connections"],
    queryFn: () => listSourceConnections(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteSourceConnection(id),
    onSuccess: () => {
      toast.success("Connection deleted");
      queryClient.invalidateQueries({ queryKey: ["source-connections"] });
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(err?.message || "Failed to delete connection"),
  });

  const revalidateMutation = useMutation({
    mutationFn: (id) => validateSourceConnection(id),
    onSuccess: () => {
      toast.success("Validation started");
      queryClient.invalidateQueries({ queryKey: ["source-connections"] });
    },
    onError: (err) => toast.error(err?.message || "Failed to start validation"),
  });

  const handleRowClick = useCallback((id) => navigate(`/source-connections/${id}`), [navigate]);
  const handleEdit = useCallback((conn) => navigate(`/source-connections/${conn.id}`), [navigate]);
  const handleDelete = useCallback((conn) => setDeleteTarget(conn), []);
  const handleRevalidate = useCallback((id) => revalidateMutation.mutate(id), [revalidateMutation]);

  const connectionsList = useMemo(() => connections || [], [connections]);

  const search = searchQuery.trim();
  const searchLower = search.toLowerCase();
  const filteredConnections = useMemo(
    () => (searchLower ? connectionsList.filter((c) => connectionHaystack(c).includes(searchLower)) : connectionsList),
    [connectionsList, searchLower]
  );

  // Contextual subtitle under the table title — reflects the live search match count + term.
  const matchCount = filteredConnections.length;
  const noun = matchCount === 1 ? "connection" : "connections";
  const hint = isLoading
    ? "Loading…"
    : search
      ? `Showing ${matchCount} ${noun} matching "${search}"`
      : `Showing ${matchCount} ${noun}`;

  return (
    <Fragment>
      <AdministrationHeader
        title="Source Connections"
        className="text-text-3"
        icon={icon({ name: "plug", style: "light" })}
        showPeriodSelector={false}
      />
      <div className="w-full p-8">
        {!isLoading && !error && connectionsList.length > 0 && (
          <div className="mb-4">
            <ConnectionsSummaryStrip connections={connectionsList} />
          </div>
        )}

        <TableCard
          title="Source Connections"
          subtitle={hint}
          primaryAction={{ label: "Add Connection", icon: icon({ name: "plus" }), onClick: () => setShowWizard(true) }}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search connections…"
          isLoading={isLoading}
          error={error}
          errorMessage="Failed to load source connections"
          onRetry={() => queryClient.invalidateQueries({ queryKey: ["source-connections"] })}
        >
          {connectionsList.length === 0 ? (
            <EmptyState
              icon={icon({ name: "plug", style: "light" })}
              title="No source connections"
              message="Add a data source to begin importing cost and usage data."
              action={{
                label: "Add Connection",
                onClick: () => setShowWizard(true),
                icon: icon({ name: "plus", style: "regular" }),
              }}
            />
          ) : filteredConnections.length === 0 ? (
            <EmptyState
              icon={icon({ name: "magnifying-glass" })}
              title="No matching connections"
              message={`No connections match "${search}".`}
              action={{
                label: "Clear search",
                onClick: () => setSearchQuery(""),
                icon: icon({ name: "xmark", style: "regular" }),
                variant: "secondary",
              }}
            />
          ) : (
            <ConnectionsTable
              data={filteredConnections}
              onRowClick={handleRowClick}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onRevalidate={handleRevalidate}
            />
          )}
        </TableCard>
      </div>

      <AddConnectionWizard
        show={showWizard}
        close={() => setShowWizard(false)}
      />

      <Modal
        show={!!deleteTarget}
        close={() => setDeleteTarget(null)}
        title="Delete Connection"
        description={`Are you sure you want to delete "${deleteTarget?.connection_name}"? This will also remove the linked import job.`}
        confirmButton="Delete"
        confirmAction={() => deleteMutation.mutate(deleteTarget.id)}
        size="sm"
      />
    </Fragment>
  );
};

export default SourceConnectionsPage;
